//@flow

import * as React from "react"
import SVGLoader from "../../UI/SVGLoader"
import { TweenMax, Back, Cubic } from "gsap"

import body from "./body.svg"
import heart from "./heart.svg"
import lungs from "./lungs.svg"
import brain from "./brain.svg"

import type { BodyPart, HotspotID } from "./types"

import Hotspot from "./Hotspot"
import { BodyContainer, BodyPartIcon } from "./UI"

type Props = {
  currentBodyPart: BodyPart,
  currentHotspot: ?HotspotID,
  onSelectHotspot: (id: ?HotspotID, anchorX: ?number, anchorY: ?number) => any
}

class Body extends React.Component<Props> {
  heart: ?HTMLElement
  brain: ?HTMLElement
  lungs: ?HTMLElement
  container: ?HTMLElement
  animations: TweenMax[]
  hotspots: { heart: HTMLElement[], brain: HTMLElement[], lungs: HTMLElement[] }

  constructor(props: Props) {
    super(props)
    this.heart = null
    this.brain = null
    this.lungs = null
    this.container = null
    this.animations = []
    this.hotspots = { heart: [], brain: [], lungs: [] }
  }

  componentDidMount() {
    const bodyParts = [this.heart, this.brain, this.lungs]
    this.colorizeBodyPart(null)

    TweenMax.staggerTo(
      bodyParts,
      0.55,
      {
        delay: 0.85,
        ease: Back.easeOut,
        y: 0
      },
      0.1
    )

    TweenMax.set(this.container, {
      opacity: 0,
      y: "340px"
    })

    TweenMax.to(this.container, 0.35, {
      opacity: 1,
      ease: Cubic.easeOut,
      delay: 0.85
    })

    TweenMax.set(
      [...this.hotspots.brain, ...this.hotspots.heart, ...this.hotspots.lungs],
      { y: 40, opacity: 0 }
    )

    this.showFullBody(0.85)
  }

  componentDidUpdate(prevProps: Props) {
    const { currentBodyPart } = this.props
    if (prevProps.currentBodyPart !== currentBodyPart) {
      switch (currentBodyPart) {
        case "neuro":
          this.showNeurologicalSystem()
          break
        case "cardio":
          this.showCardiovascularSystem()
          break
        case "respiratory":
          this.showRespiratorySystem()
          break
        default:
          this.showFullBody()
      }
    }
  }

  showFullBody = (delay: ?number) => {
    this.interruptAnimations(this.animations)
    this.hideAllHotspots()
    this.animations = [
      TweenMax.to(this.container, 0.45, {
        y: 0,
        scale: 1,
        ease: Cubic.easeOut,
        delay: delay ? delay : 0
      })
    ]
    this.colorizeBodyPart(null)
    this.props.onSelectHotspot(null, null, null)
  }

  showRespiratorySystem = () => {
    this.interruptAnimations(this.animations)
    this.hideAllHotspots()
    this.animations = [
      TweenMax.to(this.container, 0.35, {
        scale: 2,
        y: 410,
        ease: Cubic.easeInOut
      }),
      ...this.revealHotspots(this.hotspots.lungs)
    ]
    this.colorizeBodyPart(this.lungs)
  }

  showNeurologicalSystem = () => {
    this.interruptAnimations(this.animations)
    this.hideAllHotspots()
    this.animations = [
      TweenMax.to(this.container, 0.35, {
        scale: 3.2,
        y: 2660,
        ease: Cubic.easeInOut
      }),
      ...this.revealHotspots(this.hotspots.brain)
    ]
    this.colorizeBodyPart(this.brain)
  }

  showCardiovascularSystem = () => {
    this.interruptAnimations(this.animations)
    this.hideAllHotspots()
    this.animations = [
      TweenMax.to(this.container, 0.5, {
        scale: 2.8,
        y: 810,
        ease: Cubic.easeInOut
      }),
      ...this.revealHotspots(this.hotspots.heart)
    ]
    this.colorizeBodyPart(this.heart)
  }

  hideAllHotspots = () => {
    TweenMax.staggerTo(
      [
        ...this.hotspots.heart,
        ...this.hotspots.lungs,
        ...this.hotspots.brain
      ].filter(el => el !== null),
      0.1,
      { y: 40, opacity: 0 }
    )
  }

  colorizeBodyPart = (currentBodyPart: ?HTMLElement) => {
    TweenMax.set([this.heart, this.brain, this.lungs], {
      webkitFilter: "grayscale(1)",
      filter: "grayscale(1)",
      opacity: 0.42,
      zIndex: 1
    })
    currentBodyPart &&
      TweenMax.set(currentBodyPart, {
        webkitFilter: "grayscale(0)",
        filter: "grayscale(0)",
        opacity: 1,
        zIndex: 2
      })
  }

  revealHotspots = (hotspots: HTMLElement[]): any[] => {
    const actualHotspots = hotspots.filter(el => el !== null)
    TweenMax.set(actualHotspots, { scale: 0.5 })
    return [
      ...TweenMax.staggerTo(
        actualHotspots,
        0.35,
        { opacity: 1, ease: Cubic.easeOut, delay: 0.5 },
        0.25
      ),
      ...TweenMax.staggerTo(
        actualHotspots,
        0.45,
        { y: 0, ease: Back.easeOut, delay: 0.5, scale: 1 },
        0.25
      )
    ]
  }

  handleHotspotClick = (e: MouseEvent, id: HotspotID) => {
    e.stopPropagation()
    const x = e.pageX
    const y = e.pageY
    this.props.onSelectHotspot(id, x, y)
  }

  handleHotspotTouch = (e: TouchEvent, id: HotspotID) => {
    e.stopPropagation()
    const x = e.touches[0].pageX
    const y = e.touches[0].pageY
    this.props.onSelectHotspot(id, x, y)
  }

  interruptAnimations = (animations: ?(TweenMax[])) => {
    if (animations) {
      for (let animation of animations) {
        animation && animation.kill && animation.kill()
      }
    }
    this.animations = []
  }

  render() {
    const { currentHotspot } = this.props
    return (
      <BodyContainer innerRef={el => (this.container = el)}>
        <BodyPartIcon
          top="58px"
          left="262px"
          height="157px"
          width="163px"
          innerRef={el => (this.brain = el)}
        >
          <SVGLoader height="100%" width="100%" path={brain} />
        </BodyPartIcon>

        <Hotspot
          top="72px"
          left="306px"
          highlightDelay={0.75}
          selected={currentHotspot === "neuro.one"}
          innerRef={el => this.hotspots.brain.push(el)}
          onClick={(e: MouseEvent) => this.handleHotspotClick(e, "neuro.one")}
          onTouch={e => this.handleHotspotTouch(e, "neuro.one")}
        />
        <Hotspot
          top="100px"
          left="385px"
          highlightDelay={4.3}
          selected={currentHotspot === "neuro.two"}
          innerRef={el => this.hotspots.brain.push(el)}
          onClick={(e: MouseEvent) => this.handleHotspotClick(e, "neuro.two")}
          onTouch={e => this.handleHotspotTouch(e, "neuro.two")}
        />
        <Hotspot
          top="124px"
          left="290px"
          highlightDelay={2.7}
          selected={currentHotspot === "neuro.three"}
          innerRef={el => this.hotspots.brain.push(el)}
          onClick={(e: MouseEvent) => this.handleHotspotClick(e, "neuro.three")}
        />

        <BodyPartIcon
          top="414px"
          left="176px"
          height="355px"
          width="323px"
          innerRef={el => (this.lungs = el)}
        >
          <SVGLoader height="100%" width="100%" path={lungs} />
        </BodyPartIcon>

        <Hotspot
          top="464px"
          left="316px"
          highlightDelay={4.3}
          selected={currentHotspot === "respiratory.one"}
          innerRef={el => this.hotspots.lungs.push(el)}
          onClick={(e: MouseEvent) =>
            this.handleHotspotClick(e, "respiratory.one")}
        />
        <Hotspot
          top="526px"
          left="398px"
          highlightDelay={0.7}
          selected={currentHotspot === "respiratory.two"}
          innerRef={el => this.hotspots.lungs.push(el)}
          onClick={(e: MouseEvent) =>
            this.handleHotspotClick(e, "respiratory.two")}
        />
        <Hotspot
          top="624px"
          left="290px"
          highlightDelay={3.3}
          selected={currentHotspot === "respiratory.three"}
          innerRef={el => this.hotspots.lungs.push(el)}
          onClick={(e: MouseEvent) =>
            this.handleHotspotClick(e, "respiratory.three")}
        />

        <BodyPartIcon
          top="484px"
          left="282px"
          height="244px"
          width="157px"
          innerRef={el => (this.heart = el)}
        >
          <SVGLoader height="100%" width="100%" path={heart} />
        </BodyPartIcon>

        <Hotspot
          top="524px"
          left="306px"
          highlightDelay={1}
          selected={currentHotspot === "cardio.one"}
          innerRef={el => this.hotspots.heart.push(el)}
          onClick={(e: MouseEvent) => this.handleHotspotClick(e, "cardio.one")}
        />
        <Hotspot
          top="575px"
          left="380px"
          highlightDelay={4.3}
          selected={currentHotspot === "cardio.two"}
          innerRef={el => this.hotspots.heart.push(el)}
          onClick={(e: MouseEvent) => this.handleHotspotClick(e, "cardio.two")}
        />
        <Hotspot
          top="680px"
          left="326px"
          highlightDelay={3}
          selected={currentHotspot === "cardio.three"}
          innerRef={el => this.hotspots.heart.push(el)}
          onClick={(e: MouseEvent) =>
            this.handleHotspotClick(e, "cardio.three")}
        />

        <SVGLoader height="2318px" width="676px" path={body} />
      </BodyContainer>
    )
  }
}

export default Body



// WEBPACK FOOTER //
// ./src/Kiosk/BodyView/Body.js